import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { 
  OMNISTACK_BASE_URL, 
  getPrepareHeaders 
} from '@/hooks/common/common-functions'

export const routeOptimizationApi = createApi({
  reducerPath: 'routeOptimizationApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${OMNISTACK_BASE_URL}business/routes`,
    prepareHeaders: (headers) => {
      getPrepareHeaders({
        headers,
        isClientApiKey: true,
      });
      
      const businessApiKey = localStorage.getItem('businessApiKey');
      if (businessApiKey) {
        headers.set('business-x-api-key', businessApiKey);
      }
      
      return headers;
    },
  }),
  tagTypes: ['OptimizedRoutes', 'RouteMetrics', 'RouteStats', 'RouteProgress'],
  endpoints: (builder) => ({
    // ✅ Optimize routes - supports both date and month
    optimizeRoutes: builder.mutation({
      query: ({ businessId, ...optimizationData }) => ({
        url: '/optimize',
        method: 'POST',
        params: { businessId },
        body: optimizationData
      }),
      invalidatesTags: ['OptimizedRoutes', 'RouteStats']
    }),

    // ✅ UPDATED: Get optimized routes - now supports both date and month
    getOptimizedRoutes: builder.query({
      query: ({ businessId, date, month }: { businessId: string; date?: string; month?: string }) => {
        const params: any = { businessId };
        if (date) params.date = date;
        if (month) params.month = month;
        
        return {
          url: '/optimized',
          params
        };
      },
      providesTags: ['OptimizedRoutes']
    }),

    // ✅ UPDATED: Get route statistics - now supports both date and month
    getRouteStats: builder.query({
      query: ({ businessId, date, month }: { businessId: string; date?: string; month?: string }) => {
        const params: any = { businessId };
        if (date) params.date = date;
        if (month) params.month = month;
        
        return {
          url: '/stats',
          params
        };
      },
      providesTags: ['RouteStats']
    }),

    // ✅ Assign route to team
    assignRouteToTeam: builder.mutation({
      query: ({ businessId, routeId, teamId }) => ({
        url: `/${routeId}/assign`,
        method: 'POST',
        params: { businessId },
        body: { teamId }
      }),
      invalidatesTags: ['OptimizedRoutes', 'RouteProgress']
    }),

    // ✅ Update route progress
    updateRouteProgress: builder.mutation({
      query: ({ businessId, routeId, taskId, status, currentLocation }) => ({
        url: `/${routeId}/progress`,
        method: 'PUT',
        params: { businessId },
        body: { 
          taskId, 
          status,
          ...(currentLocation && { currentLocation })
        }
      }),
      invalidatesTags: ['OptimizedRoutes', 'RouteStats', 'RouteProgress']
    }),

    // ✅ Calculate route metrics
    calculateRouteMetrics: builder.query({
      query: ({ teamId, businessId, taskIds }) => ({
        url: `/${teamId}/metrics`,
        params: { businessId, taskIds: taskIds.join(',') }
      }),
      providesTags: ['RouteMetrics']
    }),

    // ✅ Re-optimize route
    reoptimizeRoute: builder.mutation({
      query: ({ routeId, businessId, params }) => ({
        url: `/${routeId}/reoptimize`,
        method: 'POST',
        params: { businessId },
        body: { params }
      }),
      invalidatesTags: ['OptimizedRoutes', 'RouteMetrics', 'RouteProgress']
    }),

    // ✅ Validate route constraints
    validateRouteConstraints: builder.query({
      query: ({ teamId, businessId, taskIds, maxTime, maxDistance }) => {
        const params = { 
          businessId, 
          taskIds: taskIds.join(','),
          ...(maxTime && { maxTime: maxTime.toString() }),
          ...(maxDistance && { maxDistance: maxDistance.toString() })
        };
        
        return {
          url: `/${teamId}/validate`,
          params
        };
      }
    }),

    // ✅ UPDATED: Get route progress - now supports both date and month
    getRouteProgress: builder.query({
      query: ({ teamId, businessId, date, month }: { teamId: string; businessId: string; date?: string; month?: string }) => {
        const params: any = { businessId };
        if (date) params.date = date;
        if (month) params.month = month;
        
        return {
          url: `/${teamId}/progress`,
          params
        };
      },
      providesTags: ['RouteProgress']
    })
  })
});

export const {
  useOptimizeRoutesMutation,
  useGetOptimizedRoutesQuery,
  useGetRouteStatsQuery,
  useAssignRouteToTeamMutation,
  useUpdateRouteProgressMutation,
  useCalculateRouteMetricsQuery,
  useReoptimizeRouteMutation,
  useValidateRouteConstraintsQuery,
  useGetRouteProgressQuery
} = routeOptimizationApi;